import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MagicWand, Trash, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const COLUMNS = {
  slabs: [
    ["slab_color", "Stone", "w-40"],
    ["material", "Material", "w-28"],
    ["finish", "Finish", "w-24"],
    ["thickness", "Thk", "w-16"],
    ["length_in", "L in", "w-16"],
    ["width_in", "W in", "w-16"],
    ["quantity", "Qty", "w-14"],
    ["cost_per_sqft", "Cost/ft²", "w-20"],
    ["sale_price_per_sqft", "Price/ft²", "w-20"],
    ["supplier", "Supplier", "w-28"],
    ["location", "Rack", "w-20"],
  ],
  sinks: [
    ["model", "Item code", "w-32"],
    ["brand", "Brand", "w-24"],
    ["sink_type", "Type", "w-28"],
    ["bowls", "Bowls", "w-24"],
    ["size", "Size", "w-28"],
    ["material", "Material", "w-28"],
    ["cost", "Cost", "w-20"],
    ["sale_price", "Price", "w-20"],
    ["quantity", "Qty", "w-14"],
    ["supplier", "Supplier", "w-28"],
  ],
};

const micro =
  "font-mono-data text-[10px] uppercase tracking-[0.22em] text-zinc-500";

export default function AiImport() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("slabs");
  const [mode, setMode] = useState("products");
  const [urls, setUrls] = useState("");
  const [limit, setLimit] = useState(12);
  const [rows, setRows] = useState(null);
  const [errors, setErrors] = useState([]);
  const [saveMode, setSaveMode] = useState("add");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const scan = async () => {
    setBusy(true);
    setRows(null);
    setErrors([]);
    try {
      const { data } = await api.post("/ai-import/scan", {
        kind,
        urls,
        mode,
        limit: Number(limit) || 12,
      });
      setRows(data.rows);
      setErrors(data.errors || []);
      if (data.count)
        toast.success(
          `${data.count} ${kind === "slabs" ? "stone" : "sink"}${
            data.count === 1 ? "" : "s"
          } read from ${data.scanned} page${data.scanned === 1 ? "" : "s"}`,
        );
      else toast.error("Nothing could be read from those links");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read those pages",
      );
    }
    setBusy(false);
  };

  const setCell = (index, field, value) =>
    setRows((all) =>
      all.map((r, n) => (n === index ? { ...r, [field]: value } : r)),
    );

  const togglePhoto = (index, photoIndex) =>
    setRows((all) =>
      all.map((r, n) =>
        n === index
          ? {
              ...r,
              photos: r.photos.map((p, i) =>
                i === photoIndex ? { ...p, keep: !p.keep } : p,
              ),
            }
          : r,
      ),
    );

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/ai-import/apply", {
        kind,
        rows,
        mode: saveMode,
      });
      toast.success(
        `${data.created} added${data.updated ? `, ${data.updated} topped up` : ""}${
          data.photos ? `, ${data.photos} photos saved` : ""
        }`,
      );
      navigate(kind === "slabs" ? "/inventory/slabs" : "/inventory/sinks");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setSaving(false);
  };

  const columns = COLUMNS[kind];

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Import by supplier link" />
      <main className="relative z-10 pb-28 pt-6" data-testid="ai-import-page">
        <Link
          to="/settings"
          data-testid="ai-import-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Import by supplier link
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Paste supplier pages and ChatGPT fills in the rows. Nothing is saved
          until you have checked it below.
        </p>

        <section className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex flex-wrap gap-4">
            <div>
              <div className={micro}>What am I importing</div>
              <div className="mt-2 flex gap-1">
                {[
                  ["slabs", "Slabs"],
                  ["sinks", "Sinks"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`ai-import-kind-${value}`}
                    onClick={() => {
                      setKind(value);
                      setRows(null);
                    }}
                    className={`h-10 rounded-lg px-4 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      kind === value
                        ? "bg-zinc-100 text-zinc-900"
                        : "border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className={micro}>What kind of link</div>
              <div className="mt-2 flex gap-1">
                {[
                  ["products", "Product pages"],
                  ["collection", "Collection page"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`ai-import-mode-${value}`}
                    onClick={() => setMode(value)}
                    className={`h-10 rounded-lg px-4 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      mode === value
                        ? "bg-zinc-100 text-zinc-900"
                        : "border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className={micro}>Most to read</div>
              <input
                data-testid="ai-import-limit"
                type="number"
                min="1"
                max="25"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="mt-2 h-10 w-24 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200 outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className={micro}>
              {mode === "collection"
                ? "Collection or category page"
                : "Product links — one per line"}
            </div>
            <textarea
              data-testid="ai-import-urls"
              rows={4}
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder={
                mode === "collection"
                  ? "https://supplier.com/collections/quartzite"
                  : "https://supplier.com/products/taj-mahal-quartzite\nhttps://supplier.com/products/calacatta-gold"
              }
              className="mt-2 w-full rounded-lg border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs text-zinc-200 outline-none focus:border-indigo-500"
            />
          </div>

          <Button
            data-testid="ai-import-scan"
            disabled={busy || !urls.trim()}
            onClick={scan}
            className="mt-4 h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <MagicWand size={15} weight="bold" className="mr-2" />
            {busy ? "Reading the pages…" : "Read the pages"}
          </Button>
          {busy && (
            <p
              className="mt-2 text-xs text-zinc-500"
              data-testid="ai-import-busy"
            >
              This can take up to a minute — it opens every page and reads it
              properly.
            </p>
          )}
        </section>

        {errors.length > 0 && (
          <div
            className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-xs text-amber-200"
            data-testid="ai-import-errors"
          >
            {errors.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </div>
        )}

        {rows && rows.length > 0 && (
          <section className="mt-4" data-testid="ai-import-preview">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-sm font-bold text-zinc-200">
                {rows.length} row{rows.length === 1 ? "" : "s"} — edit anything
                that looks wrong
              </div>
              <select
                data-testid="ai-import-save-mode"
                value={saveMode}
                onChange={(e) => setSaveMode(e.target.value)}
                className="ml-auto h-10 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
              >
                <option value="add">Add as new stock</option>
                <option value="update">Top up existing, add the rest</option>
              </select>
              <Button
                data-testid="ai-import-apply"
                disabled={saving}
                onClick={apply}
                className="h-10 bg-emerald-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-emerald-400"
              >
                <Check size={15} weight="bold" className="mr-2" />
                {saving ? "Saving…" : `Save ${rows.length}`}
              </Button>
            </div>

            <div className="mt-3 space-y-3">
              {rows.map((row, index) => (
                <div
                  key={`${row.slab_color || row.model}-${index}`}
                  data-testid={`ai-import-row-${index}`}
                  className="rounded-lg border border-zinc-800 bg-zinc-900 p-3"
                >
                  <div className="flex flex-wrap gap-2">
                    {columns.map(([field, label, width]) => (
                      <label key={field} className={`${width} min-w-[4rem]`}>
                        <span className="block text-[9px] uppercase tracking-widest text-zinc-600">
                          {label}
                        </span>
                        <input
                          data-testid={`ai-import-${index}-${field}`}
                          value={row[field] ?? ""}
                          onChange={(e) =>
                            setCell(index, field, e.target.value)
                          }
                          className="mt-1 h-9 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-200 outline-none focus:border-indigo-500"
                        />
                      </label>
                    ))}
                    <button
                      type="button"
                      data-testid={`ai-import-drop-${index}`}
                      onClick={() =>
                        setRows((all) => all.filter((_, n) => n !== index))
                      }
                      aria-label="Remove row"
                      className="mt-4 flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>

                  {row.notes && (
                    <div className="mt-2 text-xs text-zinc-500">
                      {row.notes}
                    </div>
                  )}

                  {row.photos?.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-[9px] uppercase tracking-widest text-zinc-600">
                        Photos to keep
                      </span>
                      {row.photos.map((p, photoIndex) => (
                        <button
                          key={p.url}
                          type="button"
                          data-testid={`ai-import-photo-${index}-${photoIndex}`}
                          onClick={() => togglePhoto(index, photoIndex)}
                          className={`relative h-14 w-14 overflow-hidden rounded-md border-2 transition-colors duration-150 ${
                            p.keep
                              ? "border-emerald-400"
                              : "border-zinc-800 opacity-40"
                          }`}
                        >
                          <img
                            src={p.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          {p.keep && (
                            <span className="absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center rounded-tl bg-emerald-400 text-zinc-900">
                              <Check size={10} weight="bold" />
                            </span>
                          )}
                        </button>
                      ))}
                      {row.source_url && (
                        <a
                          href={row.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-auto truncate text-[10px] text-zinc-600 hover:text-indigo-300"
                        >
                          {row.source_url}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {rows && rows.length === 0 && (
          <div
            className="mt-4 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            data-testid="ai-import-empty"
          >
            <div className="text-sm font-bold text-zinc-300">
              Nothing usable on those pages
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Try the individual product pages rather than a search or login
              page.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
